<script setup>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import { useTopicListApi, } from '@/assets/js/src/modules/topic/_composables/useTopicListApi'
import H1Title from '@/assets/js/src/components/overlay/H1Title.vue'
import { useTranslation, } from '@/assets/js/src/util/composables'
import { useHeadInfoTitle, } from '@/assets/js/src/layout/useHeadInfo'
import { getActivePinia,storeToRefs, } from 'pinia'
import { useTopicStore, } from '@/assets/js/src/modules/topic/_pinia/topic'

const activePinia = getActivePinia()
const { backToList, } = storeToRefs(useTopicStore(activePinia))

useHeadInfoTitle({
    titleKey: 'async.topic.list.title',
    descriptionKey: 'async.topic.list.description',
})

const {
    topics,
    messagesLoaded,
    allAsyncLoaded,
    asyncStatusError,
} = useTopicListApi()
const { t, } = useTranslation()
</script>

<template>
    <overlay
        class-name="topic-list"
        full-width
    >
        <v-row
            align="start"
            class="flex-wrap"
            no-gutters
        >
            <v-col
                cols="12"
                md="6"
                offset-md="3"
            >
                <template
                    v-if="allAsyncLoaded || (asyncStatusError && messagesLoaded)"
                >
                    <div class="d-flex">
                        <h1-title title-key="async.topic.list.title" />
                        <v-spacer></v-spacer>
                    </div>
                </template>
                <v-skeleton-loader
                    v-else
                    type="heading, text"
                    tile
                ></v-skeleton-loader>
            </v-col>
        </v-row>

        <!-- GRID-ANSICHT -->
        <transition-group
            v-if="allAsyncLoaded && topics.length"
            name="scale-trans"
            tag="div"
            data-resultlist
            :class="['v-row v-row--no-gutters',`topic-list--grid`]"
        >
            <v-col
                v-for="(topic, index) in [...topics,]"
                :key="'topic-' + topic.id"
                lg="2"
                md="3"
                sm="4"
                cols="6"
            >
                <router-link
                    :class="[{'first': index === 0}, 'elevation-1', `topic-list--grid__item`]"
                    :to="{path: `/topic/${topic.slug}/${topic.id}`}"
                    @click="backToList = true"
                >
                    <div
                        :class="`topic-list--grid__item--limiter`"
                    >
                        <div :class="`topic-list--grid__item--image`">
                            <bs-v-img
                                :src="((topic.metadata || {}).overview || {}).src"
                                :lazy-src="((topic.metadata || {}).lazy_thumb || {}).src"
                                :alt="topic.name"
                                :aspect-ratio="16/9"
                                :class="['secondary--text']"
                                cover
                            >
                                <template #placeholder>
                                    <v-row
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                    >
                                        <v-progress-circular
                                            indeterminate
                                            :class="['secondary--text']"
                                        ></v-progress-circular>
                                    </v-row>
                                </template>
                            </bs-v-img>
                        </div>
                        <div :class="`topic-list--grid__item--content`">
                            <h2 class="text-truncate">
                                {{ topic.name }}
                            </h2>
                        </div>
                    </div>
                </router-link>
            </v-col>
        </transition-group>
        <template
            v-else-if="allAsyncLoaded && !topics.length"
        >
            <div :class="[`topic-list--grid`, 'secondary--text']">
                {{ t('async.topic.list.no_items') }}
            </div>
        </template>
        <v-row
            v-else
            :class="[`topic-list--grid`]"
        >
            <v-col
                v-for="n in 4"
                :key="'dummy-' + n"
                :cols="6"
                :sm="4"
                :md="3"
                :lg="2"
            >
                <v-skeleton-loader
                    type="card"
                    tile
                ></v-skeleton-loader>
            </v-col>
        </v-row>
    </overlay>
</template>

<style lang="scss">
.bs-app {
    .topic-list {
        color: var(--bs-color-black);

        &__row {
            align-items: center;
            margin-bottom: map-deep-get($bs-xl, overlay, elements, marginBottom);

            .v-input--selection-controls {
                margin-top: 0;
                padding-top: 0;

                .v-label {
                    color: var(--bs-color-overlay-grey-dark);
                }
            }
        }

        &--grid {
            &__item {
                display: block;
                overflow: hidden;
                text-decoration: none;

                h2 {
                    margin: 8px 16px;
                    font-weight: 500;
                    font-size: 18px;
                }

                &:hover, &:focus {
                    h2 {
                        font-weight: 700;
                    }
                }

                &--image {
                    position: relative;

                    .v-icon {
                        position: absolute;
                        top: 8px;
                        left: 8px;
                        display: none;
                        width: 24px;
                        height: 24px;
                        background-image: radial-gradient(white 50%, transparent 50%);

                        &.status-active {
                            display: block;
                            color: map-deep-get($bs-color, bsHighlight);
                        }

                        &.status-finished {
                            display: block;
                            color: map-deep-get($bs-color, grey);
                        }
                    }
                }

                &--meta, &--intro {
                    margin: 8px 16px;

                    span {
                        color: map-deep-get($bs-color, greyDark);
                        font-size: 14px;
                    }
                }
            }
        }

        &--grid {
            margin: 0 -#{map-deep-get($bs-xl, overlay, grid, margin)};
            padding: 0 map-deep-get($bs-xl, body, paddingX);

            @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                padding: 0;
            }

            &__item {
                margin: map-deep-get($bs-xl, overlay, grid, margin);
                background-color: var(--bs-color-white);

                &--meta {
                    min-height: 81px;

                    @media (width >= 801px) and (max-width: #{map-deep-get($bs-md, breakpointValue)}px) {
                        min-height: 72px;
                    }
                }
            }
        }

        .v-skeleton-loader {
            &__text {
                height: 16px;
                margin-bottom: 24px;
            }

            &__heading {
                height: 48px;
                margin-bottom: 24px;
            }
        }

        .scale-trans {
            &-enter-from, &-leave-to {
                transform: scale(0);
                opacity: 0;
            }

            &-move {
                transition: transform .3s;
            }

            &-leave-active {
                position: absolute;
            }
        }
    }
}
</style>
